import { useEffect, useRef, useState } from "react";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { IndividualIdentityHelper } from "@/Common/Helpers/IndividualIdentityHelper";
import { PreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { Relative } from "../Models/Relative";
import { relativeStore } from "../Stores/RelativeStore";

function DemographicEdit(): JSX.Element {
    const relative = relativeStore.GetCurrent();
    const file = relativeStore.GetCurrentFile();

    const [demographics, setDemographics] = useState(file.ActiveTabSavedState
        ? file.ActiveTabSavedState // Restore saved state
        : {
            prefixLookupId: relative.PrefixLookupId,
            firstName: relative.FirstName,
            lastName: relative.LastName,
            preferredName: relative.PreferredName,
            preferredPronouns: relative.PreferredPronouns,
            specifiedPreferredPronouns: relative.SpecifiedPreferredPronouns,
            genderIdentity: relative.GenderIdentity,
            genderLookupId: relative.GenderLookupId,
            dateOfBirth: relative.DateOfBirth
        });

    const { getText } = useTranslation();

    const demographicsRef = useRef(); // State is not available on component unmount

    useEffect(() => {
        demographicsRef.current = demographics;
    }, [demographics]);

    useEffect(
        () =>
            () => {
                // Save state on unmount
                sessionStore.dispatch(LocalOperations.Relative.SaveProfileState, { state: demographicsRef.current, tab: ProfileTab.Demographic });
            }
        , [] // Empty array mean component unmount. We want the state saved once, not after every key stroke.
    );

    const onSave = (): void => {
        const updatedRelative = JSON.parse(JSON.stringify(relative)) as Relative; // Deep copy
        updatedRelative.PrefixLookupId = demographics.prefixLookupId;
        updatedRelative.FirstName = demographics.firstName;
        updatedRelative.LastName = demographics.lastName;
        updatedRelative.PreferredName = demographics.preferredName;
        updatedRelative.PreferredPronouns = demographics.preferredPronouns;
        updatedRelative.SpecifiedPreferredPronouns = demographics.specifiedPreferredPronouns;
        updatedRelative.GenderIdentity = demographics.genderIdentity;
        updatedRelative.GenderLookupId = demographics.genderLookupId;
        updatedRelative.DateOfBirth = demographics.dateOfBirth;
        relativeStore.actions.updateDemographics(updatedRelative);
    };

    return (
        <div className="tabComponent demographic">
            <div className="tabComponent-main patient-prof">
                <div className="block separated">
                    <div className="heading">
                        <h3 className="thin-font">{getText("PersonalInformationLabel")}</h3>
                    </div>
                    <div className="patient-profile fxd-wdth-container" data-cy="patientProfile-view">
                        <div className="tb">
                            <div className="tbc">
                                <table>
                                    <tbody>
                                        <tr>
                                            <td className="c1"><label className="form-label">{getText("PrefixLabel")}</label></td>
                                            <td className="c2">
                                                <SelectLookupControl
                                                    dataCy="patientProfile-patientInfo-prefix"
                                                    IncludeEmptyChoice={true}
                                                    Value={demographics.prefixLookupId}
                                                    AvailableValues={cacheStore.GetPrefixIndividualLookups().map(x => x.ToComboboxItem())}
                                                    Callback={value => setDemographics({ ...demographics, prefixLookupId: value })}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="c1"><label className="form-label">{getText("FirstNameLabel")}</label></td>
                                            <td className="c2">
                                                <TextboxControl
                                                    id="firstNameEdit"
                                                    dataCy="relativeProfile-demographic-firstName"
                                                    value={demographics.firstName}
                                                    className="form-control"
                                                    onChange={value => setDemographics({ ...demographics, firstName: value })}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="c1"><label className="form-label">{getText("LastNameLabel")}</label></td>
                                            <td className="c2">
                                                <TextboxControl
                                                    id="lastNameEdit"
                                                    dataCy="relativeProfile-demographic-lastName"
                                                    value={demographics.lastName}
                                                    className="form-control"
                                                    onChange={value => setDemographics({ ...demographics, lastName: value })}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="c1"><label className="form-label">{getText("PreferredNameLabel")}</label></td>
                                            <td className="c2">
                                                <TextboxControl
                                                    id="preferredNameEdit"
                                                    dataCy="relativeProfile-demographic-preferredName"
                                                    value={demographics.preferredName}
                                                    className="form-control"
                                                    onChange={value => setDemographics({ ...demographics, preferredName: value })}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="c1"><label className="form-label">{getText("PreferredPronounsLabel")}</label></td>
                                            <td className="c2">
                                                <SelectLookupControl
                                                    dataCy="patientProfile-patientInfo-preferredPronouns"
                                                    IncludeEmptyChoice={true}
                                                    Value={demographics.preferredPronouns?.toString()}
                                                    AvailableValues={IndividualIdentityHelper.getAvailablePreferredPronouns()}
                                                    Callback={value => setDemographics({ ...demographics, preferredPronouns: value, specifiedPreferredPronouns: null })}
                                                />
                                            </td>
                                        </tr>
                                        {demographics.preferredPronouns == PreferredPronouns.Other && (
                                            <tr>
                                                <td />
                                                <td className="c2">
                                                    <TextboxControl
                                                        id="specifiedPreferredPronounsEdit"
                                                        dataCy="patientProfile-patientInfo-specifiedPreferredPronouns"
                                                        value={demographics.specifiedPreferredPronouns}
                                                        className="form-control"
                                                        onChange={value => setDemographics({ ...demographics, specifiedPreferredPronouns: value })}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td className="c1"><label className="form-label">{getText("SexAtBirthLabel")}</label></td>
                                            <td className="c2">
                                                <SelectLookupControl
                                                    dataCy="patientProfile-patientInfo-sexAtBirth"
                                                    IncludeEmptyChoice={true}
                                                    Value={demographics.genderLookupId}
                                                    AvailableValues={cacheStore.GetGenderLookups().map(x => x.ToComboboxItem())}
                                                    Callback={value => setDemographics({ ...demographics, genderLookupId: value })}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="c1"><label className="form-label">{getText("GenderIdentityLabel")}</label></td>
                                            <td className="c2">
                                                <SelectLookupControl
                                                    dataCy="patientProfile-patientInfo-genderIdentity"
                                                    Value={demographics.genderIdentity?.toString()}
                                                    AvailableValues={IndividualIdentityHelper.getAllGenderIdentitiesAsComboBox()}
                                                    Callback={value => setDemographics({ ...demographics, genderIdentity: value })}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="c1"><label className="form-label">{getText("DateofBirthLabel")}</label></td>
                                            <td className="c2">
                                                <DateSelector
                                                    id="dob_dt"
                                                    dataCy="patientProfile-patientInfo-dt"
                                                    disabled={false}
                                                    date={demographics.dateOfBirth}
                                                    dateFormat="yyyy-MM-dd"
                                                    onChangeCallback={value => setDemographics({ ...demographics, dateOfBirth: value })}
                                                    maxDate={new Date()}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="tabComponent-footer ws-subfooter patient-profile">
                <div className="tb patient-profile-footer">
                    <div className="tbc pull-right">
                        <ButtonControl
                            className="btn btn-default push-right-5"
                            onClick={() => sessionStore.dispatch(LocalOperations.Relative.CancelProfileEdition)}
                            label={getText("CancelLabel")}
                        />
                        <ButtonControl
                            className="btn btn-primary"
                            label={getText("SaveLabel")}
                            onClick={() => onSave()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export { DemographicEdit };
